import { useState, useEffect } from 'react'
import ChatWidget from './components/ChatWidget/ChatWidget'
import BPMNLiveViewer from './components/BPMNViewer/BPMNLiveViewer'
import TemplateSelector, { WorkflowTemplate } from './components/Templates/TemplateSelector'
import BlockTester from './components/BlockTester/BlockTester'
import MobiFinLogo from './components/Logo/MobiFinLogo'
import { socketService } from './services/socketService'
import { Conversation } from './types/conversation'
import { mobifinClasses } from './theme/mobifinTheme'

function App() {
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null)
  const [showBPMN, setShowBPMN] = useState(true)
  const [showAdmin, setShowAdmin] = useState(false)
  const [showTemplates, setShowTemplates] = useState(true)
  const [showBlocks, setShowBlocks] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')

  // Log BPMN visibility state
  useEffect(() => {
    console.log('BPMN Viewer visibility:', {
      showBPMN,
      hasCurrentConversation: !!currentConversation,
      conversationHasBpmn: !!currentConversation?.bpmn,
      shouldShowViewer: showBPMN && !!currentConversation
    })
  }, [showBPMN, currentConversation])

  // Initialize WebSocket connection on mount
  useEffect(() => {
    socketService.connect()
    
    return () => {
      socketService.disconnect()
    }
  }, [])

  const handleConversationCreate = (conversation: Conversation) => {
    setCurrentConversation(conversation)
  }

  const handleConversationUpdate = (conversation: Conversation) => {
    console.log('App received conversation update:', {
      id: conversation.id,
      hasBpmn: !!conversation.bpmn,
      bpmnLength: conversation.bpmn?.length,
      stage: conversation.stage,
      progress: conversation.progress
    })
    setCurrentConversation(conversation)
  }

  const handleTemplateSelect = (template: WorkflowTemplate) => {
    console.log('Template selected:', template.title)
    console.log('Template prompt length:', template.prompt.length)
    setSelectedTemplate(template.prompt)
    setShowTemplates(false)
  }



  return (
    <div className="h-screen w-screen flex flex-col bg-gray-100">
      {/* Header */}
      <header className={`${mobifinClasses.pageHeader} flex-shrink-0`}>
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <MobiFinLogo showTagline={true} />
              <div className="w-px h-8 bg-gray-300" />
              <span className="text-sm text-gray-600">
                Conversational Workflow Design Platform
              </span>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowTemplates(!showTemplates)}
                className={`px-4 py-2 text-sm rounded-md transition-colors ${
                  showTemplates 
                    ? 'bg-[#0066CC] hover:bg-[#004499] text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {showTemplates ? 'Hide' : 'Show'} Templates
              </button>
              
              <button
                onClick={() => setShowBlocks(!showBlocks)}
                className={`px-4 py-2 text-sm rounded-md transition-colors ${
                  showBlocks 
                    ? 'bg-[#009900] hover:bg-[#006600] text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {showBlocks ? 'Hide' : 'Show'} Blocks
              </button>

              
              <button
                onClick={() => setShowBPMN(!showBPMN)}
                className={`px-4 py-2 text-sm rounded-md transition-colors ${
                  showBPMN 
                    ? 'bg-[#FF6600] hover:bg-[#CC5200] text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {showBPMN ? 'Hide' : 'Show'} BPMN
              </button>
              
              <button
                onClick={() => setShowAdmin(!showAdmin)}
                className="px-4 py-2 text-sm border-2 border-[#0066CC] text-[#0066CC] hover:bg-[#0066CC] hover:text-white rounded-md transition-colors"
              >
                Admin Panel
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 min-h-0">
        {/* Templates Section */}
        {showTemplates ? (
          <div className="h-full overflow-y-auto p-4">
            <TemplateSelector onSelectTemplate={handleTemplateSelect} />
          </div>
        ) : showBlocks ? (
          /* Blocks Testing Section */
          <div className="h-full overflow-y-auto">
            <BlockTester 
              onConversationCreate={handleConversationCreate}
              onConversationUpdate={handleConversationUpdate}
            />
          </div>
        ) : (
          /* Chat and BPMN Section */
          <div className="h-full flex">
            {/* Chat Section */}
            <div className={`${showBPMN ? 'w-1/2' : 'w-full'} h-full p-4`}>
              <ChatWidget
                conversationId={currentConversation?.id}
                onConversationCreate={handleConversationCreate}
                onConversationUpdate={handleConversationUpdate}
                showStatus={true}
                initialMessage={selectedTemplate}
              />
            </div>

            {/* BPMN Viewer Section */}
            {showBPMN && (
              <div className="w-1/2 h-full p-4 border-l bg-gray-50">
                {currentConversation ? (
                  <>
                    {console.log('Rendering BPMNLiveViewer with:', {
                      conversationId: currentConversation.id,
                      hasBpmn: !!currentConversation.bpmn,
                      bpmnLength: currentConversation.bpmn?.length
                    })}
                    <BPMNLiveViewer
                      conversationId={currentConversation.id}
                      initialBpmn={currentConversation.bpmn}
                      showControls={true}
                    />
                  </>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p>No conversation selected</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>

        {/* Admin Panel (Overlay) */}
        {showAdmin && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl w-4/5 h-4/5 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Admin Panel</h2>
                <button
                  onClick={() => setShowAdmin(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="h-full overflow-auto">
                <p className="text-gray-600">
                  Admin panel with real-time monitoring will be implemented here.
                </p>
                <ul className="mt-4 space-y-2 text-sm">
                  <li>• Active sessions monitoring</li>
                  <li>• Real-time audit trail</li>
                  <li>• Performance metrics</li>
                  <li>• System health status</li>
                </ul>
              </div>
            </div>
          </div>
        )}

      {/* Footer */}
      <footer className="bg-white border-t px-6 py-2">
        <div className="flex items-center justify-between text-xs text-gray-600">
          <div>
            Connected to: {window.location.hostname}:8080
          </div>
          <div className="flex items-center space-x-4">
            <span>WebSocket: {socketService.isConnected() ? '🟢' : '🔴'}</span>
            <span>Session: {currentConversation?.id?.slice(0, 8) || 'None'}</span>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default App